import React, { useEffect, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import classNames from 'classnames';
import { IPrintFileJob, IPrintFileLineItem, getCurrentJob, getPrintFileUrlSource } from '../../api/printFileJobs';
import {
    checkerboardSx,
    fmtBool,
    fmtDateTime,
    fmtDuration,
    IStatusStyle,
    JOB_STATUS,
    PRINT_FILE_STATUS,
    REVIEW_RESULT,
    styleFor,
    urlFileInfo,
} from '../../lib/printFileJobs/ui';
import { useStyles } from './styles';

/* ------------------------------------------------------------------ */
/* Small shared parts (also used by the collapsed row)                 */
/* ------------------------------------------------------------------ */

export function StatusBadge({ style, title }: { style: IStatusStyle; title?: string }) {
    const classes = useStyles();
    return (
        <span
            className={classes.badge}
            style={{ backgroundColor: style.color, color: style.textColor || '#fff' }}
            title={title}
        >
            {style.label}
        </span>
    );
}

function CopyButton({ text, size = 'small' }: { text: string; size?: 'small' | 'medium' }) {
    const [copied, setCopied] = useState(false);
    const handle = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (err) {
            /* clipboard unavailable */
        }
    };
    return (
        <Tooltip title={copied ? 'Copied' : 'Copy'}>
            <IconButton size={size} onClick={handle} aria-label="Copy" sx={{ p: 0.25 }}>
                <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
        </Tooltip>
    );
}

export function InfoRow({ label, value, warn }: { label: string; value: React.ReactNode; warn?: boolean }) {
    const classes = useStyles();
    return (
        <div className={classes.infoRow}>
            <span className={classes.infoLabel}>{label}</span>
            <span className={classes.infoValue} style={warn ? { color: '#d03b3b' } : undefined}>
                {value === null || value === undefined || value === '' ? '—' : value}
            </span>
        </div>
    );
}

function UrlValue({ url }: { url: string | null | undefined }) {
    const classes = useStyles();
    if (!url) return <>—</>;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 4, minWidth: 0 }}>
            <a className={classes.link} href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                {url}
            </a>
            <CopyButton text={url} />
        </span>
    );
}

interface ThumbProps {
    url: string | null | undefined;
    label?: string;
    title?: string;
    onClick?: () => void;
}

/** 52px thumbnail on a checkerboard, click opens the lightbox. */
export function ImageThumb({ url, label, title, onClick }: ThumbProps) {
    const classes = useStyles();
    const theme = useTheme();
    const [failed, setFailed] = useState(false);
    useEffect(() => setFailed(false), [url]);

    if (!url) {
        return (
            <div className={classes.thumbEmpty} title={title ?? label}>
                {label ? <span>{label}<br />—</span> : '—'}
            </div>
        );
    }
    return (
        <Tooltip title={title ?? label ?? ''}>
            <button
                type="button"
                className={classes.thumb}
                style={checkerboardSx(theme.palette.mode)}
                onClick={(e) => {
                    e.stopPropagation();
                    onClick?.();
                }}
                aria-label={`View ${title ?? label ?? 'image'}`}
            >
                {failed ? (
                    <span style={{ fontSize: 10, color: '#d03b3b' }}>error</span>
                ) : (
                    <img src={url} alt={title ?? label ?? ''} loading="lazy" onError={() => setFailed(true)} />
                )}
                {label ? <span className={classes.thumbLabel}>{label}</span> : null}
            </button>
        </Tooltip>
    );
}

interface ImageCardProps {
    title: string;
    url: string | null | undefined;
    meta?: React.ReactNode;
    onView?: () => void;
}

/** Larger preview card used inside the expanded details. */
function ImageCard({ title, url, meta, onView }: ImageCardProps) {
    const classes = useStyles();
    const theme = useTheme();
    const [failed, setFailed] = useState(false);
    const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
    useEffect(() => {
        setFailed(false);
        setNatural(null);
    }, [url]);
    const file = urlFileInfo(url);

    return (
        <div className={classes.imageCard}>
            <span className={classes.imageCardTitle}>{title}</span>
            {url ? (
                <button
                    type="button"
                    className={classes.imageCardPreview}
                    style={checkerboardSx(theme.palette.mode)}
                    onClick={(e) => {
                        e.stopPropagation();
                        onView?.();
                    }}
                    aria-label={`View ${title}`}
                >
                    {failed ? (
                        <span style={{ fontSize: 12, color: '#d03b3b' }}>Failed to load</span>
                    ) : (
                        <img
                            src={url}
                            alt={title}
                            loading="lazy"
                            onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                            onError={() => setFailed(true)}
                        />
                    )}
                </button>
            ) : (
                <div className={classes.imageCardEmpty}>no file yet</div>
            )}
            <span className={classes.imageCardMeta}>
                {url ? (
                    <>
                        {file.name}
                        {natural ? ` · ${natural.w} × ${natural.h} px` : ''}
                        {file.ext ? ` · ${file.ext.toUpperCase()}` : ''}
                    </>
                ) : (
                    'null'
                )}
                {meta ? <><br />{meta}</> : null}
            </span>
            {url ? (
                <div className={classes.imageCardActions} onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="View full size">
                        <IconButton size="small" onClick={onView} aria-label="View full size" sx={{ p: 0.5 }}>
                            <ZoomInIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Open in new tab">
                        <IconButton size="small" component="a" href={url} target="_blank" rel="noreferrer" aria-label="Open in new tab" sx={{ p: 0.5 }}>
                            <OpenInNewIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                    <CopyButton text={url} />
                    <a className={classNames(classes.link, classes.imageCardMeta)} href={url} target="_blank" rel="noreferrer" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                        {url}
                    </a>
                </div>
            ) : null}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Expanded details                                                    */
/* ------------------------------------------------------------------ */

interface Props {
    item: IPrintFileLineItem;
    /** Opens the lightbox on the image with this URL (and, if given, title). */
    onOpenImage: (url: string, title?: string) => void;
}

const SOURCE_LABEL: Record<string, string> = {
    final: 'resolved from finalPrintReadyUrl',
    draft: 'resolved from draftPrintReadyUrl',
    preprocessed: 'resolved from current job preprocessedPrintReadyUrl',
};

export default function PrintFileLineItemDetails({ item, onOpenImage }: Props) {
    const classes = useStyles();
    const theme = useTheme();
    const pf = item.printFile;
    const jobs: IPrintFileJob[] = item.jobs || [];
    const current = getCurrentJob(item);
    const source = getPrintFileUrlSource(item);
    const failedCount = jobs.filter((j) => j.status === 'failed').length;

    return (
        <Box sx={{ py: 2, px: 1 }} onClick={(e) => e.stopPropagation()}>
            {/* -------- images -------- */}
            <div className={classes.sectionTitle}>Images</div>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 1.5, mb: 3 }}>
                <ImageCard title="Original (Shopify)" url={item.originalUrl} meta="originalUrl" onView={() => item.originalUrl && onOpenImage(item.originalUrl, 'Original (Shopify)')} />
                <ImageCard title="Mockup (Shopify)" url={item.mockupUrl} meta="mockupUrl" onView={() => item.mockupUrl && onOpenImage(item.mockupUrl, 'Mockup (Shopify)')} />
                <ImageCard
                    title="Print file (resolved)"
                    url={item.printFileUrl}
                    meta={source ? SOURCE_LABEL[source] : 'printFileUrl'}
                    onView={() => item.printFileUrl && onOpenImage(item.printFileUrl, 'Print file (resolved)')}
                />
                <ImageCard title="Draft print-ready" url={pf?.draftPrintReadyUrl} meta="printFile.draftPrintReadyUrl" onView={() => pf?.draftPrintReadyUrl && onOpenImage(pf.draftPrintReadyUrl, 'Draft print-ready')} />
                <ImageCard title="Final print-ready" url={pf?.finalPrintReadyUrl} meta="printFile.finalPrintReadyUrl" onView={() => pf?.finalPrintReadyUrl && onOpenImage(pf.finalPrintReadyUrl, 'Final print-ready')} />
                <ImageCard
                    title="Background removed (current job)"
                    url={current?.backgroundRemovedUrl}
                    meta={current ? `job ${current.id} · ${current.status}` : 'no current job'}
                    onView={() => current?.backgroundRemovedUrl && onOpenImage(current.backgroundRemovedUrl, 'Background removed')}
                />
                <ImageCard
                    title="Preprocessed print-ready (current job)"
                    url={current?.preprocessedPrintReadyUrl}
                    meta={current ? `job ${current.id} · ${current.status}` : 'no current job'}
                    onView={() => current?.preprocessedPrintReadyUrl && onOpenImage(current.preprocessedPrintReadyUrl, 'Preprocessed print-ready')}
                />
            </Box>

            {/* -------- line item + print file -------- */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 3, mb: 3 }}>
                <div>
                    <div className={classes.sectionTitle}>Line item</div>
                    <InfoRow label="lineItemId" value={<span className={classes.mono}>{item.lineItemId}</span>} />
                    <InfoRow label="printReadyFileId" value={<span className={classes.mono}>{item.printReadyFileId}</span>} />
                    <InfoRow label="originalUrl" value={<UrlValue url={item.originalUrl} />} />
                    <InfoRow label="mockupUrl" value={<UrlValue url={item.mockupUrl} />} />
                    <InfoRow label="printFileUrl" value={<UrlValue url={item.printFileUrl} />} />
                    <InfoRow label="printFileUrl source" value={source ? SOURCE_LABEL[source] : '—'} />
                </div>

                <div>
                    <div className={classes.sectionTitle}>Print file</div>
                    <InfoRow label="id" value={<span className={classes.mono}>{pf?.id}</span>} />
                    <InfoRow label="orderId" value={<span className={classes.mono}>{pf?.orderId}</span>} />
                    <InfoRow label="sku" value={<span className={classes.mono}>{pf?.sku}</span>} />
                    <InfoRow
                        label="color"
                        value={
                            pf?.color ? (
                                <>
                                    <span className={classes.colorDot} style={{ backgroundColor: pf.color === 'white' ? '#fff' : pf.color === 'black' ? '#000' : pf.color }} />
                                    {pf.color}
                                </>
                            ) : null
                        }
                    />
                    <InfoRow label="printFileStatus" value={<StatusBadge style={styleFor(PRINT_FILE_STATUS, pf?.printFileStatus)} title={pf?.printFileStatus} />} />
                    <InfoRow label="reviewResult" value={pf?.reviewResult ? <StatusBadge style={styleFor(REVIEW_RESULT, pf.reviewResult)} title={pf.reviewResult} /> : null} />
                    <InfoRow label="hasManualEdits" value={fmtBool(pf?.hasManualEdits)} />
                    <InfoRow label="croppingNotes" value={pf?.croppingNotes ? <span style={{ whiteSpace: 'pre-wrap' }}>{pf.croppingNotes}</span> : null} />
                    <InfoRow label="draftPrintReadyUrl" value={<UrlValue url={pf?.draftPrintReadyUrl} />} />
                    <InfoRow label="finalPrintReadyUrl" value={<UrlValue url={pf?.finalPrintReadyUrl} />} />
                    <InfoRow label="createdAt" value={pf?.createdAt ? `${fmtDateTime(pf.createdAt)} (${pf.createdAt})` : null} />
                    <InfoRow label="updatedAt" value={pf?.updatedAt ? `${fmtDateTime(pf.updatedAt)} (${pf.updatedAt})` : null} />
                </div>
            </Box>

            {/* -------- jobs history -------- */}
            <div className={classes.sectionTitle}>
                Jobs history
                <span className={classes.muted} style={{ fontWeight: 400 }}>
                    {jobs.length} job{jobs.length === 1 ? '' : 's'}, newest first
                    {failedCount ? ` · ${failedCount} failed` : ''}
                    {current ? ` · current: ${current.id}` : ' · no current job'}
                </span>
            </div>
            {jobs.length === 0 ? (
                <Box sx={{ color: theme.palette.text.secondary, fontSize: 13, mb: 3 }}>No jobs for this print file.</Box>
            ) : (
                <Box sx={{ overflowX: 'auto', mb: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                    <Table size="small" aria-label="print file jobs history">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Job ID</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Current</TableCell>
                                <TableCell>Created</TableCell>
                                <TableCell>Finished</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell align="center">BG removed</TableCell>
                                <TableCell align="center">Preprocessed</TableCell>
                                <TableCell>Error / skip</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {jobs.map((job, i) => {
                                const st = styleFor(JOB_STATUS, job.status);
                                const hasError = !!(job.errorStep || job.errorType || job.errorMessage);
                                return (
                                    <TableRow
                                        key={job.id}
                                        sx={job.current ? { backgroundColor: theme.palette.mode === 'dark' ? 'rgba(56,189,248,0.08)' : 'rgba(4,125,149,0.06)' } : undefined}
                                    >
                                        <TableCell className={classes.muted}>{jobs.length - i}</TableCell>
                                        <TableCell>
                                            <span className={classes.mono} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                                                {job.id}
                                                <CopyButton text={job.id} />
                                            </span>
                                        </TableCell>
                                        <TableCell><StatusBadge style={st} title={job.status} /></TableCell>
                                        <TableCell>{job.current ? <strong style={{ color: theme.palette.primary.main }}>current</strong> : <span className={classes.muted}>—</span>}</TableCell>
                                        <TableCell style={{ whiteSpace: 'nowrap' }} title={job.createdAt}>{fmtDateTime(job.createdAt)}</TableCell>
                                        <TableCell style={{ whiteSpace: 'nowrap' }} title={job.finishedAt ?? ''}>{fmtDateTime(job.finishedAt)}</TableCell>
                                        <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDuration(job.createdAt, job.finishedAt)}</TableCell>
                                        <TableCell align="center">
                                            <div className={classes.thumbRow}>
                                                <ImageThumb
                                                    url={job.backgroundRemovedUrl}
                                                    title={`Background removed · job ${job.id}`}
                                                    onClick={() => job.backgroundRemovedUrl && onOpenImage(job.backgroundRemovedUrl, 'Background removed')}
                                                />
                                            </div>
                                        </TableCell>
                                        <TableCell align="center">
                                            <div className={classes.thumbRow}>
                                                <ImageThumb
                                                    url={job.preprocessedPrintReadyUrl}
                                                    title={`Preprocessed print-ready · job ${job.id}`}
                                                    onClick={() => job.preprocessedPrintReadyUrl && onOpenImage(job.preprocessedPrintReadyUrl, 'Preprocessed print-ready')}
                                                />
                                            </div>
                                        </TableCell>
                                        <TableCell style={{ minWidth: 220, maxWidth: 420 }}>
                                            {hasError ? (
                                                <div style={{ color: '#d03b3b', fontSize: 12 }}>
                                                    {job.errorStep ? <div><span className={classes.muted}>step </span>{job.errorStep}</div> : null}
                                                    {job.errorType ? <div><span className={classes.muted}>type </span>{job.errorType}</div> : null}
                                                    {job.errorMessage ? <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{job.errorMessage}</div> : null}
                                                </div>
                                            ) : null}
                                            {job.skipReason ? (
                                                <div style={{ fontSize: 12 }}>
                                                    <span className={classes.muted}>skip </span>
                                                    {job.skipReason}
                                                </div>
                                            ) : null}
                                            {!hasError && !job.skipReason ? <span className={classes.muted}>—</span> : null}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </Box>
            )}

            {/* -------- raw -------- */}
            <Accordion disableGutters sx={{ '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Raw JSON</span>
                </AccordionSummary>
                <AccordionDetails>
                    <pre className={classes.pre}>{JSON.stringify(item, null, 2)}</pre>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
}
